/**
 * FieldHistoryPopover — per-field change trail, opened by the clock icon in
 * InlineEdit. Uses Radix Dialog (already in the stack; no Popover package
 * installed). Visible to ALL authenticated users (canSeeAuditLog relaxed).
 *
 * Fetches fetchChangelog({ entityId, field, limit: 10 }) and renders a compact
 * ChangelogTimeline. Shows a "See full history" link that deep-links to
 * /history?entityId=…&field=….
 */

import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Clock, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchChangelog } from "../lib/dataFetchers";
import { ChangelogTimeline } from "./ChangelogTimeline";
import { getFieldLabel } from "../lib/types";
import { cn } from "../lib/cn";

interface Props {
  entityId: string;
  /** The InlineEdit `field` prop — matches the ChangeLog `field` column 1:1. */
  field: string;
  /** Accessible label for the trigger button (from parent). */
  triggerAriaLabel: string;
}

/** Compact wrapper shown inside the dialog body. */
function FieldHistoryBody({
  entityId,
  field,
}: {
  entityId: string;
  field: string;
}): JSX.Element {
  const { t } = useTranslation("audit");
  const fieldLabel = getFieldLabel(field);

  const { data, isLoading } = useQuery({
    queryKey: ["changelog", { entityId, field, limit: 10, hideSystem: true }],
    queryFn: () => fetchChangelog({ entityId, field, limit: 10, hideSystem: true }),
  });

  const items = data?.items ?? [];

  return (
    <div className="flex flex-col gap-3 min-h-0">
      {/* Dialog header */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[14px] font-semibold text-text">
          {t("field_history_label", { field: fieldLabel })}
        </h2>
        <Dialog.Close
          aria-label={t("common:close")}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-dim hover:text-text hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
        >
          <X className="h-4 w-4" aria-hidden />
        </Dialog.Close>
      </div>

      {/* Timeline */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="skeleton h-10" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-[13px] text-dim text-center py-4 border border-dashed border-border rounded-[9px]">
          {t("field_history_empty", { field: fieldLabel })}
        </p>
      ) : (
        <div className="overflow-y-auto max-h-[320px]">
          <ChangelogTimeline changes={items} showEntityName={false} />
        </div>
      )}

      {/* "See full history" deep-link */}
      <div className="pt-2 border-t border-border-2">
        <Link
          to={`/history?entityId=${entityId}&field=${encodeURIComponent(field)}`}
          className={cn(
            "inline-flex items-center gap-1.5 text-[12.5px] font-medium text-accent",
            "hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 rounded",
          )}
        >
          {t("see_all_in_history")}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </Link>
      </div>
    </div>
  );
}

/**
 * Renders the clock-icon trigger button. The dialog is opened inline — no
 * separate "open" state needed in the parent; Radix Dialog handles it.
 */
export function FieldHistoryPopover({ entityId, field, triggerAriaLabel }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation("common");

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          aria-label={triggerAriaLabel}
          title={t("history")}
          className={cn(
            // Matches the pencil button sizing/visibility pattern in InlineEdit
            "opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity",
            "inline-flex items-center justify-center h-[26px] w-[26px] rounded-md",
            "text-dim hover:text-accent",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30",
            // Minimum touch target on mobile
            "min-h-[44px] sm:min-h-[26px]",
          )}
        >
          <Clock className="h-[13px] w-[13px]" aria-hidden />
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-in fade-in-0" />
        <Dialog.Content
          className={cn(
            "fixed z-50",
            // Centered on desktop, bottom-sheet feel on narrow viewports
            "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            "w-[min(440px,92vw)] max-h-[80vh]",
            "bg-surface border border-border rounded-[12px] shadow-pop",
            "p-5 flex flex-col",
            "animate-in fade-in-0 zoom-in-95",
            "focus:outline-none",
          )}
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">{triggerAriaLabel}</Dialog.Title>
          {open && (
            <FieldHistoryBody entityId={entityId} field={field} />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
